import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Buildings, Globe, EnvelopeSimple, ArrowLeft, CheckCircle, Hourglass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

export default function PublisherSignupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '', description: '',
    website_url: '', support_email: user?.email || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    ax().get('/api/publisher/status').then(res => {
      if (mounted) {
        setMySubmissions(res.data?.publishers || []);
        setLoaded(true);
      }
    }).catch(() => setLoaded(true));
    return () => { mounted = false; };
  }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Publisher name is required'); return; }
    setSubmitting(true);
    try {
      const { data } = await ax().post('/api/publisher/onboard', form);
      toast.success(data.message || 'Submitted for review');
      setForm({ name: '', description: '', website_url: '', support_email: user?.email || '' });
      // Refresh list
      const status = await ax().get('/api/publisher/status').catch(() => null);
      if (status) setMySubmissions(status.data?.publishers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit publisher');
    } finally { setSubmitting(false); }
  };

  const inp = "w-full bg-[#0e0e16] border border-white/[0.08] rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/50";

  const hasPending = mySubmissions.some(p => p.pending_verification);

  return (
    <div className="min-h-screen bg-[#06060A] pb-24" data-testid="publisher-signup-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#06060A]/95 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="px-6 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9D4CDD] font-semibold">Publisher Portal</p>
            <h1 className="text-white text-base font-bold">Register your publisher</h1>
          </div>
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Intro */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#9D4CDD]/12 via-[#0e0e16] to-[#0e0e16] border border-white/[0.06] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Buildings size={18} weight="duotone" className="text-[#9D4CDD]" />
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[#9D4CDD]">Become a verified publisher</span>
          </div>
          <p className="text-white text-sm">Claim your games, manage your rule sets, and run official events on CyBoard.</p>
          <p className="text-[#666] text-xs mt-2">Submissions are reviewed by our team. You'll get publisher portal access once approved (usually within 2 business days).</p>
        </motion.div>

        {/* My submissions */}
        {loaded && mySubmissions.length > 0 && (
          <div data-testid="my-submissions">
            <h3 className="text-white text-sm font-semibold mb-2">Your submissions</h3>
            <div className="space-y-2">
              {mySubmissions.map(p => (
                <div key={p.id}
                  className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 flex items-center justify-between"
                  data-testid={`submission-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${p.verified ? 'bg-[#10B981]/15' : 'bg-[#F59E0B]/15'}`}>
                      {p.verified
                        ? <CheckCircle size={18} weight="fill" className="text-[#10B981]" />
                        : <Hourglass size={16} weight="duotone" className="text-[#F59E0B]" />}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{p.name}</p>
                      <p className="text-[10px] text-[#666]">
                        {p.verified ? 'Verified — portal access granted' :
                          p.pending_verification ? 'Pending admin review' : 'Status unknown'}
                      </p>
                    </div>
                  </div>
                  {p.verified && (
                    <button onClick={() => navigate(`/publisher/${p.id}`)}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-[#9D4CDD]/15 text-[#9D4CDD] font-semibold"
                      data-testid={`open-portal-${p.id}`}>Open portal</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        {!hasPending && (
          <div className="space-y-3" data-testid="publisher-form">
            <div>
              <label className="block text-[10px] uppercase text-[#666] mb-1 tracking-wider">Publisher name *</label>
              <input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Stonemaier Games"
                data-testid="publisher-name-input" />
            </div>

            <div>
              <label className="block text-[10px] uppercase text-[#666] mb-1 tracking-wider">Description</label>
              <textarea rows={3} className={inp + ' resize-none'}
                placeholder="Briefly describe your publisher, catalog, and team."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                data-testid="publisher-description-input" />
            </div>

            <div>
              <label className="block text-[10px] uppercase text-[#666] mb-1 tracking-wider flex items-center gap-1">
                <Globe size={12} /> Website
              </label>
              <input className={inp} type="url" placeholder="https://"
                value={form.website_url}
                onChange={e => setForm({ ...form, website_url: e.target.value })}
                data-testid="publisher-website-input" />
            </div>

            <div>
              <label className="block text-[10px] uppercase text-[#666] mb-1 tracking-wider flex items-center gap-1">
                <EnvelopeSimple size={12} /> Support email
              </label>
              <input className={inp} type="email"
                placeholder="contact@publisher.com"
                value={form.support_email}
                onChange={e => setForm({ ...form, support_email: e.target.value })}
                data-testid="publisher-email-input" />
            </div>

            <button onClick={submit} disabled={submitting || !form.name.trim()}
              className="w-full mt-2 py-3 rounded-xl bg-[#9D4CDD] text-white text-sm font-semibold hover:bg-[#7C3AED] disabled:opacity-40"
              data-testid="submit-publisher-btn">
              {submitting ? 'Submitting…' : 'Submit for review'}
            </button>
            <p className="text-[10px] text-[#444] text-center">By submitting, you agree to CyBoard's Publisher Terms.</p>
          </div>
        )}

        {hasPending && (
          <div className="bg-[#F59E0B]/8 border border-[#F59E0B]/20 rounded-xl p-4 text-center" data-testid="pending-notice">
            <Hourglass size={20} weight="duotone" className="text-[#F59E0B] mx-auto mb-2" />
            <p className="text-white text-sm font-semibold">Submission in review</p>
            <p className="text-[#888] text-xs mt-1">You can submit another publisher once your current submission is approved or rejected.</p>
          </div>
        )}
      </div>
    </div>
  );
}
